import { getIpAddress } from '@api/auth/login';
import { expertsPageLog, getAllExperts, getExpertsTopics } from '@api/experts';
import useGoNative from '@hooks/useGoNative';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { ExpertTopicInterface, ExpertsResultsResponse } from '@interfaces/index';
import { Autocomplete, Box, Grid, Skeleton, TextField } from '@mui/material';
import { MotionText } from '@shared/atoms';
import { LoadingBackdrop, SystemButton } from '@shared/molecules';
import { ExpertCard, ExpertSession, ExpertSessionHistory } from '@shared/organisms';
import { CoreLayout } from '@shared/templates';
import { getHourBufferCategory } from '@utils/date.util';
import { CustomAutocompletePopper, CustomPopper, FLIP_POPPER_MODS } from '@utils/mui.util';
import {
  AvailabilityTimeNames,
  EXPERTS_TIME_FILTERS,
  ExpertLogTypes,
  USER_NOT_FOUND_IMAGE,
  castResponse,
} from 'custom.d';
import moment from 'moment-timezone';
import React, { FunctionComponent, useEffect, useState } from 'react';
import styles from './index.module.scss';

const MuiStyles = {
  autocomplete: {
    width: '100%',
    minWidth: '280px',
    marginTop: '-4px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '40px',
      height: '42px',
      border: '3px solid #019df2',
      '& .MuiInputBase-input': {
        color: '#019df2',
        fontWeight: 600,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#019df2',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#019df2',
      },
      '& .MuiAutocomplete-popupIndicator': {
        color: '#019df2',
      },
    },
    '@media (max-width: 600px)': {
      width: '100%',
      minWidth: '200px',
    },
  },
};

export const AllExperts: FunctionComponent = () => {
  const { isDesktop, isTablet } = useWindowDimensions();
  const [experts, setExperts] = useState<ExpertsResultsResponse>({ results: [], total: 0 });
  const [expertsLoading, setExpertsLoading] = useState(false);
  const [expertPage, setExpertPage] = useState<number>(0);
  const [expertPerPage] = useState<number>(30);
  const isMobileApp = useGoNative();
  const [starExpertTopicsDataLoading, setExpertTopicsDataLoading] = useState(true);
  const [expertTopics, setExpertTopics] = useState<{ topicId: number; topic: string }[] | []>([
    { topicId: -999, topic: 'Expertise' },
  ]);
  const [selectedExpertTopic, setSelectedExpertTopic] = useState<{
    topicId: number;
    topic: string;
  }>({ topicId: -999, topic: 'Expertise' });
  const [showBookingHistory, setShowBookingHistory] = useState(false);
  const [loadingAnnouncement, setLoadingAnnouncement] = useState('');

  const [availabilityTimes, setAvailabilityTimes] = useState<any>(EXPERTS_TIME_FILTERS);
  const [selectedAvailabilityTime, setSelectedAvailabilityTime] = useState<{
    id: number;
    name: string;
    value: {
      start: string;
      end: string;
    } | null;
  }>({
    id: -9999,
    name: AvailabilityTimeNames.AVAILABILITY,
    value: null,
  });

  useEffect(() => {
    getStarQuestions();
    logExperts();
  }, []);

  useEffect(() => {
    getExperts();
  }, [selectedExpertTopic, expertPage, selectedAvailabilityTime]);

  useEffect(() => {
    if (expertsLoading) {
      setLoadingAnnouncement('Loading experts, please wait.');
    } else if (experts?.results && experts?.results.length > 0) {
      setLoadingAnnouncement(`${experts.results.length} experts found.`);
    } else {
      setLoadingAnnouncement('No experts found.');
    }
  }, [expertsLoading, experts]);

  const getStarQuestions = async () => {
    try {
      setExpertTopicsDataLoading(true);
      const response = await getExpertsTopics();
      if (response?.success) {
        const data: ExpertTopicInterface[] = response?.responseObject || [];
        const updatedResponse = data.map((dt) => ({
          topicId: dt.id,
          topic: dt.topic,
        }));
        updatedResponse.unshift({ topicId: -999, topic: 'Expertise' });
        setExpertTopics(updatedResponse);
      } else {
        setExpertTopics([]);
      }
    } catch (error) {
      setExpertTopics([]);
    } finally {
      setExpertTopicsDataLoading(false);
    }
  };

  const getExperts = async () => {
    try {
      setExpertsLoading(true);

      const response = await getAllExperts(
        selectedExpertTopic.topicId === -999 ? null : selectedExpertTopic.topicId,
        null,
        expertPage,
        expertPerPage,
        selectedAvailabilityTime.value,
      );

      if (response?.success) {
        const data = castResponse<ExpertsResultsResponse>(response);
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const userTimeZoneTime = moment.tz(userTimezone).format('YYYY-MM-DD HH:mm:ss');

        const sortedExperts = (data?.results || [])
          .map((expert) => {
            const zoneName = expert?.expertDetail?.zoneName;
            const expertTimeZoneTime = moment.tz(zoneName).format('YYYY-MM-DD HH:mm:ss');

            const differenceInMinutes = moment(expertTimeZoneTime).diff(
              moment(userTimeZoneTime),
              'minutes',
            );

            return {
              ...expert,
              hourBufferCategory: getHourBufferCategory(differenceInMinutes),
            };
          })
          .sort((a, b) => {
            if (a.hourBufferCategory !== b.hourBufferCategory) {
              return a.hourBufferCategory - b.hourBufferCategory;
            }

            return b.orderId - a.orderId;
          });

        if (expertPage > 0) {
          setExperts((prevExperts) => ({
            results: [...prevExperts.results, ...sortedExperts],
            total: data?.total,
          }));
        } else {
          setExperts({ results: sortedExperts, total: data?.total });
        }
      } else {
        setExperts({ results: [], total: 0 });
      }
    } catch {
      setExperts({ results: [], total: 0 });
    } finally {
      setExpertsLoading(false);
    }
  };

  const logExperts = async () => {
    const ip = await getIpAddress();
    const req = await expertsPageLog(
      ExpertLogTypes.EXPERTS_PAGE_VISITED,
      ip || null,
      navigator.userAgentData?.platform || '',
      isMobileApp,
    );
  };

  const handleNextPage = () => {
    if (experts.results.length < experts.total) {
      setExpertPage((prev) => prev + 1);
      setLoadingAnnouncement('Loading more experts, please wait.');
    }
  };

  const handlePreviousPage = () => {
    if (expertPage > 0) {
      setExpertPage((prev) => prev - 1);
    }
  };

  const handleChange = (event: any, newValue: any) => {
    setSelectedExpertTopic(newValue || { id: -999, name: 'Expertise' });
    setExpertTopicsDataLoading(false);
    setLoadingAnnouncement(`Filtering by ${newValue?.topic || 'Expertise'}, please wait.`);
  };

  const handleChangeAvailability = (event: any, newValue: any) => {
    if (newValue.value) {
      const userTimezone = moment.tz.guess();

      const startUtc = moment
        .tz(newValue.value.start, 'HH:mm:ss', userTimezone)
        .utc()
        .format('HH:mm:ss');

      const endUtc = moment
        .tz(newValue.value.end, 'HH:mm:ss', userTimezone)
        .utc()
        .format('HH:mm:ss');

      const convertedAvailabilityTime = {
        ...newValue,
        value: {
          start: startUtc,
          end: endUtc,
        },
      };
      setSelectedAvailabilityTime(convertedAvailabilityTime);
      setLoadingAnnouncement(`Filtering by availability time ${newValue.name}, please wait.`);
    } else {
      setSelectedAvailabilityTime({
        id: -999,
        name: AvailabilityTimeNames.AVAILABILITY,
        value: null,
      });
      setLoadingAnnouncement('Clearing availability filter, please wait.');
    }
  };

  const handleClearFilters = () => {
    setSelectedExpertTopic({ topicId: -999, topic: 'Expertise' });
    setSelectedAvailabilityTime({
      id: -9999,
      name: AvailabilityTimeNames.AVAILABILITY,
      value: null,
    });
    setLoadingAnnouncement('Clearing all filters, please wait.');
  };

  const handleKeyPress = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  const toggleBookingHistory = () => {
    setShowBookingHistory((prev: boolean) => !prev);
    setLoadingAnnouncement(
      showBookingHistory ? 'Closing booking history.' : 'Opening booking history.',
    );
  };

  return (
    <CoreLayout title="Experts" navigationType="default" fullWidthEnabled>
      {/* {expertsLoading && <LoadingBackdrop text="Empowering your journey with expert guidance…" />} */}

      <div className={styles.mainContainer}>
        <Box className="sr-only" aria-live="polite" sx={{ display: 'none' }}>
          {loadingAnnouncement}
        </Box>

        <section className={styles.topBox} aria-labelledby="experts-introduction">
          <div className="container">
            <div className={styles.content}>
              <h1 id="experts-introduction" className={styles.titleText}>
                Experts
              </h1>

              <MotionText
                as="p"
                className={styles.middleText}
                text="We understand that taking charge of your wellbeing is a personal journey, and finding the right support makes all the difference. We have curated a diverse team of experts, each passionate about helping you thrive."
              />

              <MotionText
                as="p"
                className={styles.middleText}
                text="Browse profiles of our experts, each showcasing their areas of specialisation and
                approach. Find someone who resonates with your needs and preferences. When
                you're ready to take the next step, simply book your appointment directly
                through the platform."
              />
            </div>
          </div>
          <div className={styles.abstractImg} role="presentation" aria-hidden="true" />
        </section>

        <div className="container" id="experts-content">
          <section className={styles.expertSessionContainer} aria-label="Current Expert Session">
            <ExpertSession />
          </section>

          <section className={styles.filters} role="search" aria-label="Filter Options">
            <div className={styles.filterBox}>
              <p id="filter-heading">Filter by</p>

              <div>
                <Autocomplete
                  isOptionEqualToValue={(option, value) => option.topicId === value.topicId}
                  loading={starExpertTopicsDataLoading}
                  includeInputInList
                  filterSelectedOptions
                  autoHighlight
                  onOpen={() => {
                    setExpertTopicsDataLoading(true);
                    setTimeout(() => {
                      setExpertTopicsDataLoading(false);
                    }, 1000);
                  }}
                  disableClearable
                  options={expertTopics}
                  getOptionLabel={(option) => option.topic}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label=""
                      sx={MuiStyles.autocomplete}
                      aria-label="Filter by expertise"
                    />
                  )}
                  value={selectedExpertTopic}
                  onChange={handleChange}
                  sx={MuiStyles.autocomplete}
                  className={styles.autoComplete}
                  disablePortal
                  PopperComponent={isDesktop ? CustomPopper : CustomAutocompletePopper}
                  componentsProps={FLIP_POPPER_MODS}
                />
              </div>

              <div>
                <Autocomplete
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  includeInputInList
                  filterSelectedOptions
                  autoHighlight
                  disableClearable
                  options={EXPERTS_TIME_FILTERS}
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label=""
                      sx={MuiStyles.autocomplete}
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: 'off',
                        readOnly: true,
                      }}
                      aria-label="Filter by availability"
                    />
                  )}
                  value={selectedAvailabilityTime}
                  onChange={handleChangeAvailability}
                  sx={MuiStyles.autocomplete}
                  className={styles.autoComplete2}
                  disablePortal
                  PopperComponent={isDesktop ? CustomPopper : CustomAutocompletePopper}
                  componentsProps={FLIP_POPPER_MODS}
                />
              </div>
            </div>
            <div className={styles.bookingHistoryBtn}>
              <SystemButton
                variant="outlinedPrimarySkyBlue"
                className={styles.hsSecondary}
                onClick={toggleBookingHistory}
                aria-expanded={showBookingHistory}
                aria-controls="booking-history-section"
              >
                Your Booking History
              </SystemButton>
            </div>
          </section>

          {showBookingHistory && (
            <section
              className={styles.expertSessionHistoryContainer}
              id="booking-history-section"
              aria-live="polite"
            >
              <ExpertSessionHistory handleHistoryOpen={setShowBookingHistory} />
            </section>
          )}

          <section aria-label="Expert search results">
            {expertsLoading ? (
              <Grid container spacing={4} className={styles.expertGrid}>
                {Array.from({ length: isDesktop ? 12 : isTablet ? 2 : 1 }).map((_, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <Skeleton
                      variant="rounded"
                      width="100%"
                      height={400}
                      key={index}
                      className={styles.itemLoading}
                      aria-label="Loading expert profile"
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Grid container spacing={4} className={styles.expertGrid}>
                {experts.results.map((data, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <ExpertCard
                      id={data.id}
                      image={
                        data?.expertDetail?.profileImage
                          ? data.expertDetail.profileImage
                          : USER_NOT_FOUND_IMAGE
                      }
                      name={`${data.firstName} ${data.lastName}`}
                      description={data?.expertDetail?.headline || ''}
                      experience={`${data?.expertDetail?.experience || 0} years experience`}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

            {experts.total === 0 && !expertsLoading && (
              <div className={styles.noExperts} aria-live="assertive">
                <MotionText
                  as="p"
                  text=" No experts found with these filters. Try adjusting availability or specialty
                filters to see more options."
                />

                <button
                  onClick={handleClearFilters}
                  onKeyDown={(e) => handleKeyPress(e, handleClearFilters)}
                >
                  clear filters
                </button>
              </div>
            )}

            {experts?.results?.length < experts?.total && (
              <nav className={styles.pagination} aria-label="Pagination">
                <SystemButton
                  variant="primaryDarkSkyBlue"
                  disabled={experts.results.length < expertPerPage}
                  onClick={handleNextPage}
                  aria-label={`Load more experts (${experts.results.length} of ${experts.total} shown)`}
                >
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Load more&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                </SystemButton>
              </nav>
            )}
          </section>
        </div>
      </div>
    </CoreLayout>
  );
};
